import {
  Box,
  Checkbox,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import type { IntentQuestion } from "@shared/companyIntent.types";
import {
  isIntentQuestionComplete,
  serializeIntentAnswer,
  toIntentAnswerPayload,
} from "@shared/companyIntentAnswers.util";
import {
  Q15_GROUP_PRIORITY_COUNTRIES,
  Q15_PRIORITY_MARKET_ID,
  getMaxSelections,
  hasAnswerGroups,
  resolveIntentSelectionMode,
  resolveQ15RegionIdsFromOptionTags,
} from "@shared/intentQuestionnaireDimensions";
import { displayOptionsForIntentQuestion } from "@shared/intentQuestionOptions.util";
import type { IntentAnswerValue } from "@shared/companyIntent.types";
import IntentAnswerGroupsFields from "./IntentAnswerGroupsFields";

export type QuestionAnswerState = {
  selectedIds: string[];
  followUpText?: string;
  groupSelections?: Record<string, string[]>;
  groupRegionIds?: Record<string, string[]>;
  conditionalAnswers?: Record<string, string | string[]>;
};

type IntentQuestionCardProps = {
  question: IntentQuestion;
  index: number;
  answer: QuestionAnswerState;
  isDesktop: boolean;
  onChange: (next: QuestionAnswerState) => void;
};

function selectionHint(question: IntentQuestion, displayOptions: IntentQuestion["options"]): string {
  const maxSelections = getMaxSelections(question.id) ?? question.maxSelections;
  const usesCatalogGroups =
    hasAnswerGroups(question.id) &&
    Boolean(question.answerGroups?.some((g) => g.inputType === "catalog_geography"));
  const usesRenderableGroups =
    hasAnswerGroups(question.id) && Boolean(question.answerGroups?.length);

  if (usesCatalogGroups) {
    return "Choose a priority region. Countries are optional";
  }

  if (usesRenderableGroups && !displayOptions.length) {
    const allSingle = question.answerGroups!.every((g) => g.selectionMode === "single");
    if (allSingle) return "Choose one option in each section";
    return "Answer each section below";
  }

  const mode = resolveIntentSelectionMode(question.id, question.selectionMode);
  if (maxSelections != null && mode === "multiple") {
    return `Select up to ${maxSelections} options`;
  }
  return mode === "single" ? "Choose one" : "Select all that apply";
}

export function isQuestionAnswerStateComplete(
  question: IntentQuestion,
  answer: QuestionAnswerState,
): boolean {
  const usesAnswerGroups = hasAnswerGroups(question.id) && Boolean(question.answerGroups?.length);
  const payload = toIntentAnswerPayload({
    selectedIds: answer.selectedIds,
    selectionMode: resolveIntentSelectionMode(question.id, question.selectionMode),
    maxSelections: getMaxSelections(question.id) ?? question.maxSelections,
    followUpText: answer.followUpText,
    groupSelections: answer.groupSelections,
    groupRegionIds: answer.groupRegionIds,
    conditionalAnswers: answer.conditionalAnswers,
    usesAnswerGroups,
  });
  return isIntentQuestionComplete(question, payload as IntentAnswerValue);
}

export default function IntentQuestionCard({
  question,
  index,
  answer,
  isDesktop,
  onChange,
}: IntentQuestionCardProps) {
  const displayOptions = displayOptionsForIntentQuestion(question);
  const mode = resolveIntentSelectionMode(question.id, question.selectionMode);
  const maxSelections = getMaxSelections(question.id) ?? question.maxSelections;
  const answered = isQuestionAnswerStateComplete(question, answer);
  const selectedIds = answer.selectedIds;
  const usesAnswerGroups = hasAnswerGroups(question.id) && Boolean(question.answerGroups?.length);
  const isQ15Hybrid = usesAnswerGroups && displayOptions.length > 0;

  const toggleOption = (optionId: string) => {
    if (mode === "single") {
      const nextAnswer: QuestionAnswerState = { ...answer, selectedIds: [optionId] };

      if (question.id === Q15_PRIORITY_MARKET_ID) {
        const option = displayOptions.find((o) => o.id === optionId);
        const regionIds = option ? resolveQ15RegionIdsFromOptionTags(option.tags) : [];
        if (regionIds.length > 0) {
          nextAnswer.groupRegionIds = {
            ...(answer.groupRegionIds ?? {}),
            [Q15_GROUP_PRIORITY_COUNTRIES]: regionIds,
          };
          nextAnswer.groupSelections = {
            ...(answer.groupSelections ?? {}),
            [Q15_GROUP_PRIORITY_COUNTRIES]: [],
          };
        }
      }

      onChange(nextAnswer);
      return;
    }

    if (selectedIds.includes(optionId)) {
      onChange({
        ...answer,
        selectedIds: selectedIds.filter((id) => id !== optionId),
      });
      return;
    }

    const nextSelected = serializeIntentAnswer([...selectedIds, optionId], "multiple", maxSelections);
    onChange({ ...answer, selectedIds: nextSelected });
  };

  const renderOptionGrid = (
    options: IntentQuestion["options"],
    checkedIds: string[],
    onToggle: (id: string) => void,
    idPrefix: string,
    gridMode: "single" | "multiple" = mode,
  ) => (
    <SimpleGrid cols={isDesktop ? 2 : 1} spacing="sm" verticalSpacing="sm" mt="md">
      {options.map((opt, optIndex) => {
        const checked = checkedIds.includes(opt.id);
        const optionKey = String.fromCharCode(65 + optIndex);
        const atMax =
          gridMode === "multiple" &&
          maxSelections != null &&
          !checked &&
          checkedIds.length >= maxSelections;

        return (
          <Box
            key={`${idPrefix}-${opt.id}`}
            role={gridMode === "single" ? "radio" : "checkbox"}
            aria-checked={checked}
            aria-disabled={atMax}
            tabIndex={atMax ? -1 : 0}
            onClick={() => {
              if (atMax) return;
              onToggle(opt.id);
            }}
            onKeyDown={(event) => {
              if (atMax) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onToggle(opt.id);
              }
            }}
            style={{
              padding: "12px 14px",
              borderRadius: "var(--mantine-radius-md)",
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "var(--mantine-color-primary-7)",
              backgroundColor: checked
                ? "var(--mantine-color-primary-7)"
                : "var(--mantine-color-white)",
              boxShadow: checked ? "0 6px 16px -8px rgba(4, 48, 92, 0.55)" : "none",
              transition: "all 150ms ease",
              cursor: atMax ? "not-allowed" : "pointer",
              opacity: atMax ? 0.55 : 1,
            }}
          >
            <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
              <Group gap="xs" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                <Box
                  style={{
                    flexShrink: 0,
                    width: 24,
                    height: 24,
                    borderRadius: gridMode === "single" ? "50%" : 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                    border: `1px solid ${
                      checked ? "rgba(255, 255, 255, 0.6)" : "var(--mantine-color-primary-7)"
                    }`,
                    backgroundColor: checked ? "rgba(255, 255, 255, 0.18)" : "transparent",
                    color: checked ? "#ffffff" : "var(--mantine-color-primary-7)",
                  }}
                >
                  {optionKey}
                </Box>
                <Text
                  size="sm"
                  c={checked ? "white" : "primary.7"}
                  fw={checked ? 600 : 500}
                  style={{
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    lineHeight: 1.35,
                  }}
                >
                  {opt.label}
                </Text>
              </Group>
              {gridMode === "multiple" ? (
                <Checkbox
                  checked={checked}
                  disabled={atMax}
                  onChange={() => {}}
                  tabIndex={-1}
                  color="primary"
                  styles={{
                    root: { flexShrink: 0, pointerEvents: "none" },
                    input: { pointerEvents: "none" },
                    inner: { pointerEvents: "none" },
                  }}
                />
              ) : null}
            </Group>
          </Box>
        );
      })}
    </SimpleGrid>
  );

  return (
    <Box
      style={{
        borderRadius: 16,
        border: `1px solid ${
          answered ? "var(--mantine-color-primary-3)" : "var(--mantine-color-primary-1)"
        }`,
        backgroundColor: "var(--mantine-color-white)",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04), 0 6px 16px -10px rgba(16, 24, 40, 0.18)",
        padding: "var(--mantine-spacing-lg)",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
      }}
    >
      <Group align="flex-start" gap="sm" wrap="nowrap" mb={4}>
        <ThemeIcon
          size={28}
          radius="xl"
          variant={answered ? "filled" : "light"}
          color={answered ? "teal" : "primary"}
          style={{ flexShrink: 0 }}
        >
          {answered ? (
            <IconCheck size={16} />
          ) : (
            <Text size="xs" fw={700} c="primary.7">
              {index + 1}
            </Text>
          )}
        </ThemeIcon>
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text fw={600} size="sm" c="slate.8" style={{ lineHeight: 1.4 }}>
            {question.prompt}
          </Text>
          <Text size="xs" c="slate.5">
            {selectionHint(question, displayOptions)}
          </Text>
        </Stack>
      </Group>

      {isQ15Hybrid ? (
        <>
          {renderOptionGrid(displayOptions, selectedIds, toggleOption, question.id)}
          <IntentAnswerGroupsFields
            question={question}
            answer={answer}
            onChange={onChange}
            renderOptionGrid={renderOptionGrid}
          />
        </>
      ) : usesAnswerGroups ? (
        <>
          {displayOptions.length > 0
            ? renderOptionGrid(displayOptions, selectedIds, toggleOption, question.id)
            : null}
          <IntentAnswerGroupsFields
            question={question}
            answer={answer}
            onChange={onChange}
            renderOptionGrid={renderOptionGrid}
          />
        </>
      ) : (
        renderOptionGrid(displayOptions, selectedIds, toggleOption, question.id)
      )}
    </Box>
  );
}
